import React, { FormEventHandler, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import log from 'loglevel'
import { RpcError } from 'grpc-web'
import { ComplianceFramework } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import { useHideModal, useIsShowModal } from 'src/Modal/ModalStateContext'
import { FrameworkSectionsEditor } from 'src/components/ModalForms/Frameworks/FrameworkSectionsEditor'
import {
  getDuplicateNameErrors,
  getEmptyNameErrors,
  parseBackendDuplicateError,
} from 'src/components/ModalForms/Frameworks/FrameworkSectionsEditor.helpers'
import {
  FrameworkSectionsEditorErrors,
  SectionInfo,
} from 'src/components/ModalForms/Frameworks/FrameworkSectionsEditor.types'
import {
  useEditFrameworkSection,
  useExistingFrameworkNames,
  useFrameworkControls,
} from 'src/pages/Frameworks/frameworks.hooks'
import { getErrorMessage } from 'src/Utils/globalHelpers'
import { ModalForm, ModalFormId } from '../..'

type Props = {
  framework: ComplianceFramework
  selectedSection: SectionInfo | null
  clearSelectedSection: () => void
}

export const EditFrameworkSectionModal = ({
  framework,
  selectedSection,
  clearSelectedSection,
}: Props): JSX.Element => {
  const show = useIsShowModal(ModalFormId.EDIT_FRAMEWORK_SECTION)
  const hideModal = useHideModal({
    modalId: ModalFormId.EDIT_FRAMEWORK_SECTION,
  })
  const editFrameworkSection = useEditFrameworkSection()
  const { controlOptions, isLoading: controlsLoading } =
    useFrameworkControls(show)
  const { existingSectionNames, existingRequirementNames } =
    useExistingFrameworkNames(framework, show)
  const [sectionDraft, setSectionDraft] = useState<SectionInfo[]>([])
  const [editErrors, setEditErrors] = useState<FrameworkSectionsEditorErrors>(
    {},
  )
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!show || !selectedSection) return
    setSectionDraft([selectedSection])
    setEditErrors({})
    setIsSubmitting(false)
  }, [selectedSection, show])

  // Names from OTHER sections (so the editor's own draft can rename freely
  // within this section without false-positive collision flags).
  const otherSectionNames = useMemo(
    () => existingSectionNames.filter((n) => n !== selectedSection?.name),
    [existingSectionNames, selectedSection],
  )
  const thisSectionRequirementNames = useMemo(
    () => new Set((selectedSection?.requirements ?? []).map((r) => r.name)),
    [selectedSection],
  )
  // Requirement names not in this section. We exclude the names of this
  // section's existing requirements (the editor recheck dupes within the
  // section as the user types).
  const otherRequirementNames = useMemo(
    () =>
      existingRequirementNames.filter(
        (n) => !thisSectionRequirementNames.has(n),
      ),
    [existingRequirementNames, thisSectionRequirementNames],
  )

  const errors: FrameworkSectionsEditorErrors = useMemo(
    () => ({
      sectionNames: editErrors.sectionNames,
      requirementNames: editErrors.requirementNames,
    }),
    [editErrors],
  )

  const closeAndReset = () => {
    hideModal()
    setSectionDraft([])
    setEditErrors({})
    setIsSubmitting(false)
    clearSelectedSection()
  }

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault()
    if (!selectedSection || sectionDraft.length === 0) return
    setEditErrors({})
    const sectionModelId = selectedSection.modelId || ''

    const empty = getEmptyNameErrors(sectionDraft)
    const dupes = getDuplicateNameErrors(
      sectionDraft,
      otherSectionNames,
      otherRequirementNames,
    )
    const sectionNames = { ...empty.sectionNames, ...dupes.sectionNames }
    const requirementNames = {
      ...empty.requirementNames,
      ...dupes.requirementNames,
    }
    if (
      Object.keys(sectionNames).length ||
      Object.keys(requirementNames).length
    ) {
      setEditErrors({ sectionNames, requirementNames })
      return
    }

    setIsSubmitting(true)
    try {
      const edited = sectionDraft[0]
      await editFrameworkSection({
        sectionModelId,
        name: edited.name,
        details: edited.description,
        requirements: edited.requirements,
      })
      toast.success('Section updated.')
      closeAndReset()
    } catch (err) {
      const dupResult = parseBackendDuplicateError(err, sectionDraft)
      if (dupResult) {
        if (dupResult.editorErrors) setEditErrors(dupResult.editorErrors)
        if (dupResult.toast) toast.error(dupResult.toast)
        return
      }
      const errorMessage = getErrorMessage(err as RpcError)
      toast.error(errorMessage.message)
      if (errorMessage.shouldLog) {
        log.error('EditFrameworkSectionModal submit failed', err)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ModalForm
      show={show}
      hide={closeAndReset}
      formId={ModalFormId.EDIT_FRAMEWORK_SECTION}
      size="xl"
      title="Edit Section"
      submitText="Save"
      showOverflow
      disableSubmitButton={
        isSubmitting ||
        controlsLoading ||
        !selectedSection ||
        sectionDraft.length === 0
      }
    >
      <form id={ModalFormId.EDIT_FRAMEWORK_SECTION} onSubmit={onSubmit}>
        <FrameworkSectionsEditor
          sections={sectionDraft}
          setSections={setSectionDraft}
          setErrors={setEditErrors}
          controlOptions={controlOptions}
          errors={errors}
          disableSectionCrud
        />
      </form>
    </ModalForm>
  )
}
